import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Button, Label } from "reactstrap";
import CustomisedModal from "../../../components/Modal/CustomisedModal";
import { commonConfirmBox } from "../../../helper/messagehelpers";
import { ApiResponseMessage } from "../../../helper/tosterHelper";
import { DANGER } from "../../../constant/commonConstants";
import { AddSvg, BackSvg, SearchSvg } from "../../../components/CommonSvg";
import {
    getVendorDocumentsRequest,
    uploadVendorDocumentRequest,
    deleteVendorDocumentRequest,
    updateVendorDocumentStatusRequest,
} from "../../../redux/vendor/documents/action";
import UploadDocument from "../../Vendor/Documents/UploadDocument";
import ShareDocumentModal from "../../Vendor/Documents/ShareDocumentModal";
import DocumentDetailsModal from "../../Vendor/Documents/DocumentDetailsModal";
import DocumentFolderList from "./DocumentFolderList";
import DocumentListRow from "./DocumentListRow";
import { useDocumentShare } from "../../Vendor/Documents/documentShareApi";
import { PER_PAGE, downloadDocument } from "../../Vendor/Documents/documentUtils";

const DocumentManagement = ({
    list,
    categories,
    counts,
    vendors,
    totalRecords,
    totalPages,
    loading,
    uploading,
    uploadResult,
    statusUpdating,
    getVendorDocuments,
    uploadVendorDocument,
    deleteVendorDocument,
    updateVendorDocumentStatus,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchData, setSearchData] = useState("");
    const [opened, setOpened] = useState(null);
    const [modalView, setModalView] = useState(false);
    const [shareFor, setShareFor] = useState(null);
    const [viewFor, setViewFor] = useState(null);
    const [menuFor, setMenuFor] = useState(null);

    /* an open kebab menu closes on any click elsewhere on the page, same as
       the vendor header's user menu */
    useEffect(() => {
        if (!menuFor) return undefined;
        const close = () => setMenuFor(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [menuFor]);

    const category = opened === null ? "" : opened;

    const openFolder = (folderId) => {
        setOpened(folderId);
        setCurrentPage(1);
        setSearchData("");
    };

    const closeFolder = () => {
        setOpened(null);
        setCurrentPage(1);
        setSearchData("");
    };

    const listQuery = useMemo(
        () => ({
            category: category,
            searchText: searchData,
            pagination: { page: currentPage, limit: PER_PAGE },
        }),
        [category, searchData, currentPage]
    );

    const { targets, loadTargets, share, sharing } = useDocumentShare(listQuery);

    useEffect(() => {
        getVendorDocuments(listQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listQuery]);

    useEffect(() => {
        if (uploadResult?.status) {
            setModalView(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadResult?.at]);

    const totalFiles = Object.values(counts).reduce(
        (total, count) => total + Number(count || 0),
        0
    );

    const openFolderName =
        opened === null || opened === ""
            ? "All Documents"
            : categories.find((folder) => folder.id === opened)?.name || "Documents";

    const handleDownload = async (row) => {
        const result = await downloadDocument(row);
        if (!result.status) {
            ApiResponseMessage(result.message, DANGER);
        }
    };

    const handleDelete = async (row) => {
        const confirmed = await commonConfirmBox(
            `Do you want to delete "${row.documentTitle}"?`
        );
        if (confirmed) {
            deleteVendorDocument({
                documentId: row.documentId,
                listQuery: listQuery,
            });
        }
    };

    const handleToggleStatus = async (row) => {
        const confirmed = await commonConfirmBox(
            "",
            undefined,
            undefined,
            row.isActive
                ? `Do you want to deactivate "${row.documentTitle}"?`
                : `Do you want to activate "${row.documentTitle}"?`
        );
        if (confirmed) {
            updateVendorDocumentStatus({
                documentId: row.documentId,
                isActive: !row.isActive,
                listQuery: listQuery,
            });
        }
    };

    /* the list refetches after a toggle, so the modal has to track the row by
       id and pick up the fresh copy rather than holding onto the stale one it
       was opened with */
    const viewRow =
        viewFor && (list.find((row) => row.documentId === viewFor.documentId) || viewFor);

    return (
        <>
            <div className="container">
                <div className="section-head">
                    <div className="back-arrow_button">
                        {opened !== null && (
                            <Button onClick={closeFolder} outline>
                                <BackSvg />
                            </Button>
                        )}
                        <h3>
                            {opened === null ? "Document Management" : openFolderName}
                        </h3>
                    </div>

                    <div className="header-action-item">
                        {opened !== null && (
                            <div className="doc-search">
                                <SearchSvg />
                                <input
                                    type="text"
                                    value={searchData}
                                    placeholder="Search documents…"
                                    onChange={(event) => {
                                        setSearchData(event.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        )}
                        <button
                            className="creat_btn btn btn-secondary"
                            onClick={() => setModalView(true)}
                        >
                            <AddSvg />
                            Upload Document
                        </button>
                    </div>
                </div>

                {opened === null ? (
                    <DocumentFolderList
                        categories={categories}
                        counts={counts}
                        totalFiles={totalFiles}
                        onOpen={openFolder}
                    />
                ) : (
                    <>
                        <div className="doc-list">
                            {list.map((row) => (
                                <DocumentListRow
                                    key={row.documentId}
                                    row={row}
                                    showCategoryTag={category === ""}
                                    statusUpdating={statusUpdating}
                                    menuOpen={menuFor === row.documentId}
                                    onToggleMenu={(current) =>
                                        setMenuFor((open) =>
                                            open === current.documentId
                                                ? null
                                                : current.documentId
                                        )
                                    }
                                    onDetails={(current) => {
                                        setViewFor(current);
                                        setMenuFor(null);
                                    }}
                                    onShare={(current) => {
                                        setShareFor(current);
                                        setMenuFor(null);
                                    }}
                                    onDownload={(current) => {
                                        handleDownload(current);
                                        setMenuFor(null);
                                    }}
                                    onDelete={(current) => {
                                        handleDelete(current);
                                        setMenuFor(null);
                                    }}
                                    onToggleStatus={handleToggleStatus}
                                />
                            ))}

                            {list.length === 0 && (
                                <div className="doc-empty">
                                    {loading
                                        ? "Loading documents…"
                                        : searchData
                                        ? "No documents match your search."
                                        : "No documents in this folder yet."}
                                </div>
                            )}
                        </div>

                        {totalPages > 1 && (
                            <div className="doc-pager">
                                <button
                                    type="button"
                                    disabled={currentPage <= 1}
                                    onClick={() =>
                                        setCurrentPage((current) => current - 1)
                                    }
                                >
                                    Previous
                                </button>
                                <span>
                                    Page {currentPage} of {totalPages} · {totalRecords}{" "}
                                    {totalRecords === 1 ? "document" : "documents"}
                                </span>
                                <button
                                    type="button"
                                    disabled={currentPage >= totalPages}
                                    onClick={() =>
                                        setCurrentPage((current) => current + 1)
                                    }
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {modalView && (
                <CustomisedModal
                    modalView={modalView}
                    modalName={"Upload Document"}
                    onModalDismiss={() => setModalView(false)}
                >
                    <UploadDocument
                        categories={categories}
                        uploading={uploading}
                        defaultCategory={category}
                        isAdmin
                        vendors={vendors}
                        onCancel={() => setModalView(false)}
                        onSubmit={(formData) =>
                            uploadVendorDocument({
                                formData: formData,
                                listQuery: listQuery,
                            })
                        }
                    />
                </CustomisedModal>
            )}

            {shareFor && (
                <CustomisedModal
                    modalView={!!shareFor}
                    modalName={"Share Document"}
                    onModalDismiss={() => setShareFor(null)}
                >
                    <ShareDocumentModal
                        key={shareFor.documentId}
                        document={shareFor}
                        {...targets}
                        sharing={sharing}
                        onLoadTargets={loadTargets}
                        onCancel={() => setShareFor(null)}
                        onShared={() => setShareFor(null)}
                        onSubmit={(data) => share(data)}
                    />
                </CustomisedModal>
            )}

            {viewFor && (
                <CustomisedModal
                    modalView={!!viewFor}
                    modalName={"Document Details"}
                    onModalDismiss={() => setViewFor(null)}
                >
                    <DocumentDetailsModal
                        document={viewRow}
                        onDownload={handleDownload}
                        extraRows={
                            viewRow?.uploadBy === "Admin" && (
                                <div className="doc-view-block">
                                    <Label className="doc-view-klabel">Visible To</Label>
                                    <div className="doc-view-value">
                                        {viewRow?.visibleToAllVendors
                                            ? "All Vendors"
                                            : viewRow?.visibleVendorNames?.join(", ") ||
                                              "—"}
                                    </div>
                                </div>
                            )
                        }
                    />
                </CustomisedModal>
            )}
        </>
    );
};

const mapStateToProps = ({ VendorDocuments: documents }) => {
    return {
        list: documents?.list || [],
        categories: documents?.categories || [],
        counts: documents?.counts || {},
        vendors: documents?.vendors || [],
        totalRecords: documents?.totalRecords || 0,
        totalPages: documents?.totalPages || 0,
        loading: documents?.loading || false,
        uploading: documents?.uploading || false,
        uploadResult: documents?.uploadResult || null,
        statusUpdating: documents?.statusUpdating || false,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVendorDocuments: (data) => dispatch(getVendorDocumentsRequest(data)),
        uploadVendorDocument: (data) => dispatch(uploadVendorDocumentRequest(data)),
        deleteVendorDocument: (data) => dispatch(deleteVendorDocumentRequest(data)),
        updateVendorDocumentStatus: (data) =>
            dispatch(updateVendorDocumentStatusRequest(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentManagement);
